import React from 'react';
import { Link } from 'react-router-dom';

import {
  AccountManager,
  BetManager,
  constants,
  getConfigProp,
  isEmptyCollection,
  isUserActionNeedPhotoVerification,
  isUserNeedVerification,
  ObservableOddsProvider,
  PhotoVerificationAction,
  SiteConfigManager,
  useApplicationState,
  useBetslipState,
  useGlobalBetslipState,
} from '@atlas/core';
import { staticRoutes } from '@atlas/routing';
import { Button } from '@atlas/ui';

import NotificationStatePopup from 'common/Popup/NotificationStatePopup';
import { debounce } from 'common/utils/debounce';
import VerificationButton from 'components/Verification/VerificationButton';

import AcceptOddsChangesSelect from './BetsMoneyBlock/AcceptOddsChangesSelect';
import BetslipSignInButton from './BetsMoneyBlock/BetslipSignInButton';
// Betslip european components
import BetsList from './BetslipEuropeanBetItemsList';
import BetslipEuropeanPlaceBetButton from './BetslipEuropeanPlaceBetButton';
import { useLocalBetslip } from './LocalBetslipContext';

const validateBets = debounce(bets => {
  BetManager.validateBets(bets);
}, 1000);

const BetslipClassic = () => {
  const { active, amount, betItems, fullfited, loading, settings, validationStatus } =
    useBetslipState();
  const { reports } = useGlobalBetslipState();
  const { account, authenticated, wallet } = useApplicationState();
  const currency = AccountManager.getAccountCurrency();
  const { setIsOpenPopup } = useLocalBetslip();
  const isSportRadarProject = getConfigProp('isSportRadarProject');

  const isPlaceBetButtonActive = React.useMemo(
    () => betItems.some(bet => bet.active && !bet.suspended),
    [betItems],
  );

  const betAmountWarning = React.useMemo(() => betItems.every(bet => !bet.amount), [betItems]);

  // Used for displaying clear all
  const isBetItemsAvailable = betItems.length > 1;

  const handlePlaceBet = React.useCallback(() => {
    // Placing bets
    BetManager.placeBets(betItems.filter(bet => bet.requestId && bet.active && bet.amount > 0));
  }, [betItems]);

  const handleClearAll = React.useCallback(() => {
    BetManager.clearBets();
    setIsOpenPopup(false);
  }, [setIsOpenPopup]);

  // Betslip validation ready effect
  React.useEffect(() => {
    // Betslip settings check
    if (settings.validationDisabled) {
      return;
    }
    // validating only if authenticated
    // bets data fetched
    // bets active
    // betslip not loading
    if (authenticated && fullfited && active && !loading) {
      // Verifying if selected bet exist
      const bets = betItems.filter(bet => bet.requestId && bet.active && !bet.suspended);
      if (!bets.length) {
        return;
      }

      // Checking if betslip waits for validation
      if (validationStatus === constants.BETSLIP_VALIDATION_STATUS.WAITING_VALIDATION) {
        validateBets.cancel();
        validateBets(bets);
      }
    }
  }, [
    validationStatus,
    authenticated,
    fullfited,
    active,
    loading,
    betItems,
    settings.validationDisabled,
  ]);

  if (isEmptyCollection(betItems)) {
    if (!isEmptyCollection(reports)) {
      return null;
    }

    const buttonsNotificationPopup = (
      <>
        {SiteConfigManager.getConfig('config.config.enable_registration') && (
          <Link className="secondary" to={staticRoutes.signup}>
            <Button
              label="Sign up"
              onClick={() => setIsOpenPopup(false)}
              size="big"
              type="secondary"
            />
          </Link>
        )}
        <Link className="primary" to={staticRoutes.signin}>
          <Button label="Sign in" onClick={() => setIsOpenPopup(false)} size="big" type="primary" />
        </Link>
      </>
    );

    return (
      <NotificationStatePopup
        absolute={false}
        buttons={!authenticated && buttonsNotificationPopup}
        message="No bets have been selected. To select a bet click in a respective odd."
        title="You have not added outcomes"
      />
    );
  }

  return (
    <ObservableOddsProvider bets={betItems}>
      <div className="betslip-classic-container">
        <div className="bet-items">
          <div className="bet-items__list">
            <BetsList />
          </div>
        </div>

        <div className="betslip-classic-controls">
          {isBetItemsAvailable && (
            <div className="betslip-controls-multiple">
              <div className="betslip-controls-clear">
                <Button
                  disabled={loading}
                  icon="trash-can"
                  label="clean_betslip"
                  loading={loading}
                  onClick={handleClearAll}
                  type="text"
                />
              </div>
            </div>
          )}

          <div className="bottom-button-container">
            {!isSportRadarProject && <AcceptOddsChangesSelect />}

            {authenticated ? (
              isUserNeedVerification(account) &&
              isUserActionNeedPhotoVerification(account, PhotoVerificationAction.BET_SLIP) ? (
                <VerificationButton onClick={() => setIsOpenPopup(false)} />
              ) : (
                <BetslipEuropeanPlaceBetButton
                  balance={wallet.balance}
                  betAmount={amount}
                  betAmountWarning={betAmountWarning}
                  currency={currency}
                  disabled={!isPlaceBetButtonActive}
                  onSubmit={handlePlaceBet}
                />
              )
            ) : (
              <BetslipSignInButton />
            )}
          </div>
        </div>
      </div>
    </ObservableOddsProvider>
  );
};

export default BetslipClassic;
